import { useMemo, useState, useEffect, useCallback } from 'react';
import { Filter, SlidersHorizontal, X, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { MultiSelect } from '@/components/ui/multiselect';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { useFilters } from '@/contexts/FilterContext';
import { useScheduler } from '@/contexts/SchedulerContext';
import { AdvancedFilterDialog } from './AdvancedFilterDialog';
import { PROJECT_STATUSES } from '@/lib/statuses';
import { CalendarFilters, DEFAULT_FILTERS, AdvancedFilters, DEFAULT_ADVANCED_FILTERS } from '@/types/filters';

// ─── Trigger Button ───────────────────────────────────────────────────────────

interface CalendarFilterDrawerTriggerProps {
  activeFilterCount: number;
  onClick: () => void;
}

export function CalendarFilterDrawerTrigger({
  activeFilterCount,
  onClick,
}: CalendarFilterDrawerTriggerProps) {
  const { filterStats, hasActiveFilters, hasActiveAdvancedFilters } = useFilters();
  const showBadge = (hasActiveFilters || hasActiveAdvancedFilters) && filterStats;

  return (
    <div className="relative inline-flex">
      <Button
        variant={activeFilterCount > 0 ? 'default' : 'outline'}
        size="sm"
        onClick={onClick}
        className="h-8 w-8 p-0"
        title={
          activeFilterCount > 0
            ? `${activeFilterCount} filter${activeFilterCount !== 1 ? 's' : ''} active`
            : 'Filters'
        }
      >
        <Filter className="h-3.5 w-3.5" />
      </Button>
      {showBadge && (
        <span className="absolute -top-3 -right-3 inline-flex items-center rounded-full bg-destructive px-2 py-1 text-xs font-semibold leading-none text-destructive-foreground shadow whitespace-nowrap pointer-events-none">
          {filterStats.filteredBookings} / {filterStats.totalBookings}
        </span>
      )}
    </div>
  );
}

// ─── Simple filter field ──────────────────────────────────────────────────────

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold text-foreground">{label}</Label>
      {children}
    </div>
  );
}

// ─── Section heading (for standalone labels like Utilisation / Advanced) ─────

function SectionHeading({ label }: { label: string }) {
  return (
    <Label className="text-xs font-bold text-foreground block">{label}</Label>
  );
}

// ─── Main Drawer ──────────────────────────────────────────────────────────────

interface CalendarFilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CalendarFilterDrawer({ open, onOpenChange }: CalendarFilterDrawerProps) {
  const {
    filters,
    setFilters,
    resetFilters,
    hasActiveFilters,
    filterStats,
    hasActiveAdvancedFilters,
    advancedFilters,
    setAdvancedFilters,
    resetAdvancedFilters,
    showFilteredBookings,
    setShowFilteredBookings,
    hasHiddenBookings,
  } = useFilters();

  const { settings, resources, projectDetails, costCentres } = useScheduler();
  const [advancedDialogOpen, setAdvancedDialogOpen] = useState(false);

  // ── Draft state: changes are staged here until "Apply Filters" is clicked ──
  const [draftFilters, setDraftFilters] = useState<CalendarFilters>(filters);
  const [draftAdvancedFilters, setDraftAdvancedFilters] = useState<AdvancedFilters>(advancedFilters);

  useEffect(() => {
    if (open) {
      setDraftFilters(filters);
      setDraftAdvancedFilters(advancedFilters);
    }
  }, [open, filters, advancedFilters]);

  const updateDraftFilter = useCallback(<K extends keyof CalendarFilters>(key: K, value: CalendarFilters[K]) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ── Derived options (based on draft state so UI stays responsive) ───────────

  const groupField =
    settings.groupingMethod === 'scheduler-group' ? 'schedulerGroup' : 'department';
  const groupLabel =
    settings.groupingMethod === 'scheduler-group' ? 'Planner Groups' : 'Departments';

  const DEPARTMENTS = useMemo(() => {
    let base = resources;
    if (draftFilters.roles.length > 0) base = base.filter((r) => draftFilters.roles.includes(r.role));
    if (draftFilters.resources.length > 0) base = base.filter((r) => draftFilters.resources.includes(r.id));
    const unique = new Set<string>();
    base.forEach((r) => {
      const v = r[groupField as keyof typeof r] as string | undefined;
      if (v) unique.add(v);
    });
    return Array.from(unique).sort().map((v) => ({ value: v, label: v }));
  }, [resources, draftFilters.roles, draftFilters.resources, groupField]);

  const AVAILABLE_RESOURCES = useMemo(() => {
    let base = resources;
    if (draftFilters.departments.length > 0)
      base = base.filter((r) => {
        const v = r[groupField as keyof typeof r] as string | undefined;
        return v && draftFilters.departments.includes(v);
      });
    if (draftFilters.roles.length > 0) base = base.filter((r) => draftFilters.roles.includes(r.role));
    return base
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map((r) => ({ value: r.id, label: r.name }));
  }, [resources, draftFilters.departments, draftFilters.roles, groupField]);

  const AVAILABLE_ROLES = useMemo(() => {
    let base = resources;
    if (draftFilters.departments.length > 0)
      base = base.filter((r) => {
        const v = r[groupField as keyof typeof r] as string | undefined;
        return v && draftFilters.departments.includes(v);
      });
    if (draftFilters.resources.length > 0)
      base = base.filter((r) => draftFilters.resources.includes(r.id));
    const unique = new Set<string>();
    base.forEach((r) => { if (r.role) unique.add(r.role); });
    return Array.from(unique).sort().map((v) => ({ value: v, label: v }));
  }, [resources, draftFilters.departments, draftFilters.resources, groupField]);

  const AVAILABLE_PROJECTS = useMemo(() => {
    let opts = projectDetails
      .map((p) => ({
        value: String(p.id),
        label: p.name && p.billingCode ? `${p.name} (${p.billingCode})` : p.name || String(p.id),
        status: p.status,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (draftFilters.projectStatuses.length > 0)
      opts = opts.filter((o) => o.status && draftFilters.projectStatuses.includes(o.status));
    opts.unshift({ value: 'no-project', label: 'No Project assigned' } as typeof opts[number]);
    return opts;
  }, [projectDetails, draftFilters.projectStatuses]);

  const AVAILABLE_COST_CENTRES = useMemo(() => {
    let base = costCentres;
    const selectedProjectIds = draftFilters.projects.filter((p) => p !== 'no-project');
    if (selectedProjectIds.length > 0) {
      const selectedBillingCodes = new Set(
        projectDetails
          .filter(p => selectedProjectIds.includes(String(p.id)))
          .map(p => p.billingCode)
          .filter(Boolean)
      );
      base = base.filter(
        (cc) => cc.projectCode != null && selectedBillingCodes.has(cc.projectCode)
      );
    }
    let opts = base
      .map((cc) => ({
        value: String(cc.id),
        label:
          cc.name && cc.code ? `${cc.name} (${cc.code})` : cc.name || cc.code || String(cc.id),
        status: cc.status,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if (draftFilters.costCentreStatuses.length > 0)
      opts = opts.filter((o) => o.status && draftFilters.costCentreStatuses.includes(o.status));
    opts.unshift({
      value: 'no-cost-centre',
      label: 'No Cost Centre assigned',
    } as typeof opts[number]);
    return opts;
  }, [costCentres, draftFilters.projects, draftFilters.costCentreStatuses, projectDetails]);

  const AVAILABLE_BOOKING_TYPES = useMemo(() => {
    const opts = (settings.bookingTypes || [])
      .map((bt) => ({ value: String(bt.id), label: `[${bt.code}] ${bt.name}` }))
      .sort((a, b) => a.label.localeCompare(b.label));
    opts.unshift({ value: 'no-booking-type', label: 'No Booking Type assigned' });
    return opts;
  }, [settings.bookingTypes]);

  const BOOKING_STATUSES = useMemo(
    () =>
      settings.statusConfigs
        .filter((s) => s.enabled)
        .map((s) => ({ value: String(s.id), label: s.customText || s.text })),
    [settings.statusConfigs]
  );

  const PROJECT_STATUS_OPTIONS = useMemo(
    () => PROJECT_STATUSES.map((s) => ({ value: s.value, label: s.label })),
    []
  );

  // ── Draft vs applied comparison ────────────────────────────────────────────

  const hasDraftChanged = useMemo(() => {
    const filtersChanged =
      JSON.stringify(draftFilters) !== JSON.stringify(filters);
    const advancedChanged =
      JSON.stringify(draftAdvancedFilters) !== JSON.stringify(advancedFilters);
    return filtersChanged || advancedChanged;
  }, [draftFilters, filters, draftAdvancedFilters, advancedFilters]);

  const hasDraftFiltersSet = useMemo(() => {
    return (
      draftFilters.departments.length > 0 ||
      draftFilters.resources.length > 0 ||
      draftFilters.roles.length > 0 ||
      (draftFilters.utilisationRange[0] !== 0 || draftFilters.utilisationRange[1] !== 100) ||
      draftFilters.overUtilisedOnly ||
      draftFilters.projects.length > 0 ||
      draftFilters.projectStatuses.length > 0 ||
      draftFilters.costCentres.length > 0 ||
      draftFilters.costCentreStatuses.length > 0 ||
      draftFilters.bookingTypes.length > 0 ||
      draftFilters.bookingStatuses.length > 0 ||
      draftAdvancedFilters.conditions.length > 0
    );
  }, [draftFilters, draftAdvancedFilters]);

  // ── Active filter count (based on draft for the UI display) ────────────────

  const isUtilisationActive =
    draftFilters.utilisationRange[0] !== 0 ||
    draftFilters.utilisationRange[1] !== 100 ||
    draftFilters.overUtilisedOnly;

  const draftHasAdvancedFilters = draftAdvancedFilters.conditions.length > 0;

  const activeFilterCount =
    draftFilters.departments.length +
    draftFilters.resources.length +
    draftFilters.roles.length +
    draftFilters.projects.length +
    draftFilters.costCentres.length +
    draftFilters.bookingTypes.length +
    draftFilters.bookingStatuses.length +
    (isUtilisationActive ? 1 : 0) +
    (draftHasAdvancedFilters ? draftAdvancedFilters.conditions.length : 0);

  const canApply = hasDraftChanged;
  const canClearAll = hasActiveFilters || hasActiveAdvancedFilters || hasDraftFiltersSet;

  // ── Batch apply / clear handlers ───────────────────────────────────────────

  const handleApplyFilters = useCallback(() => {
    setFilters(draftFilters);
    setAdvancedFilters(draftAdvancedFilters);
    onOpenChange(false);
  }, [draftFilters, draftAdvancedFilters, setFilters, setAdvancedFilters, onOpenChange]);

  const handleClearAll = useCallback(() => {
    setDraftFilters(DEFAULT_FILTERS);
    setDraftAdvancedFilters(DEFAULT_ADVANCED_FILTERS);
    resetFilters();
    resetAdvancedFilters();
  }, [resetFilters, resetAdvancedFilters]);

  // Track which sections are visible to know where to place dividers
  const showSchedulerGroups = DEPARTMENTS.length > 0;
  const showRoles = AVAILABLE_ROLES.length > 0;
  const showResources = AVAILABLE_RESOURCES.length > 0;
  const showProjectStatus = AVAILABLE_PROJECTS.length > 0;
  const showProjects = AVAILABLE_PROJECTS.length > 0;
  const showCostCentres = AVAILABLE_COST_CENTRES.length > 0;
  const showBookingType = AVAILABLE_BOOKING_TYPES.length > 0;
  const showBookingStatus = BOOKING_STATUSES.length > 0;

  // A helper to decide if a divider is needed between two sections
  const resourcesSectionVisible = showSchedulerGroups || showRoles || showResources;
  const projectsSectionVisible = showProjectStatus || showProjects;
  const bookingsSectionVisible = showBookingType || showBookingStatus;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-[380px] sm:max-w-[380px] flex flex-col p-0 gap-0"
        >
          {/* Header */}
          <SheetHeader className="px-4 pt-4 pb-3 border-b shrink-0 gap-0">
            <div className="flex items-center gap-2">
              <SheetTitle className="text-sm font-semibold">Filters</SheetTitle>
              <Badge
                variant="secondary"
                className="flex items-center gap-1 px-2.5 py-0.5 h-6 text-[11px] font-medium rounded-full bg-primary/10 text-primary border-0 hover:bg-primary/10"
              >
                <Filter className="h-3 w-3" />
                {activeFilterCount} filters active
              </Badge>
            </div>

            {/* Result counts — only when filters are active */}
            {filterStats && (hasActiveFilters || hasActiveAdvancedFilters) && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {filterStats.filteredResources} of {filterStats.totalResources} resources
                &nbsp;·&nbsp;
                {filterStats.filteredBookings} of {filterStats.totalBookings} bookings
              </p>
            )}
          </SheetHeader>

          {/* Scrollable filter list */}
          <ScrollArea className="flex-1">
            <ScrollAreaViewport>
              <div className="px-4 py-4 space-y-4">

                {/* ── Resources group ── */}
                {showSchedulerGroups && (
                  <Field label={groupLabel}>
                    <MultiSelect
                      options={DEPARTMENTS}
                      value={draftFilters.departments}
                      onValueChange={(v) => updateDraftFilter('departments', v)}
                      placeholder={`All ${groupLabel}`}
                    />
                  </Field>
                )}

                {showRoles && (
                  <Field label="Roles">
                    <MultiSelect
                      options={AVAILABLE_ROLES}
                      value={draftFilters.roles}
                      onValueChange={(v) => updateDraftFilter('roles', v)}
                      placeholder="All Roles"
                    />
                  </Field>
                )}

                {showResources && (
                  <Field label="Resources">
                    <MultiSelect
                      options={AVAILABLE_RESOURCES}
                      value={draftFilters.resources}
                      onValueChange={(v) => updateDraftFilter('resources', v)}
                      placeholder="All Resources"
                    />
                  </Field>
                )}

                {/* Divider after Resources group */}
                {resourcesSectionVisible && (projectsSectionVisible || showCostCentres || bookingsSectionVisible) && (
                  <Separator />
                )}

                {/* ── Projects group ── */}
                {showProjectStatus && (
                  <Field label="Project Status">
                    <MultiSelect
                      options={PROJECT_STATUS_OPTIONS}
                      value={draftFilters.projectStatuses}
                      onValueChange={(v) => updateDraftFilter('projectStatuses', v)}
                      placeholder="All Statuses"
                    />
                  </Field>
                )}

                {showProjects && (
                  <Field label="Projects">
                    <MultiSelect
                      options={AVAILABLE_PROJECTS}
                      value={draftFilters.projects}
                      onValueChange={(v) => {
                        updateDraftFilter('projects', v);
                        if (draftFilters.costCentres.length > 0) updateDraftFilter('costCentres', []);
                      }}
                      placeholder="All Projects"
                    />
                  </Field>
                )}

                {/* Divider after Projects group */}
                {projectsSectionVisible && (showCostCentres || bookingsSectionVisible) && (
                  <Separator />
                )}

                {/* ── Cost Centres ── */}
                {showCostCentres && (
                  <Field label="Cost Centres">
                    <MultiSelect
                      options={AVAILABLE_COST_CENTRES}
                      value={draftFilters.costCentres}
                      onValueChange={(v) => updateDraftFilter('costCentres', v)}
                      placeholder="All Cost Centres"
                    />
                  </Field>
                )}

                {/* Divider after Cost Centres */}
                {showCostCentres && bookingsSectionVisible && (
                  <Separator />
                )}

                {/* ── Bookings group ── */}
                {showBookingType && (
                  <Field label="Booking Type">
                    <MultiSelect
                      options={AVAILABLE_BOOKING_TYPES}
                      value={draftFilters.bookingTypes}
                      onValueChange={(v) => updateDraftFilter('bookingTypes', v)}
                      placeholder="All Booking Types"
                    />
                  </Field>
                )}

                {showBookingStatus && (
                  <Field label="Booking Status">
                    <MultiSelect
                      options={BOOKING_STATUSES}
                      value={draftFilters.bookingStatuses}
                      onValueChange={(v) => updateDraftFilter('bookingStatuses', v)}
                      placeholder="All Statuses"
                    />
                  </Field>
                )}

                {/* Divider before Utilisation */}
                {(resourcesSectionVisible || projectsSectionVisible || showCostCentres || bookingsSectionVisible) && (
                  <Separator />
                )}

                {/* ── Utilisation ── */}
                <div className="space-y-3 pt-1">
                  <SectionHeading label="Utilisation" />

                  {/* Over-utilised toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-foreground">Over utilised only</span>
                    <Switch
                      checked={draftFilters.overUtilisedOnly}
                      onCheckedChange={(v) => updateDraftFilter('overUtilisedOnly', v)}
                    />
                  </div>

                  {/* Range slider */}
                  <div className="space-y-2 px-0.5">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>Range</span>
                      <span className="tabular-nums font-medium text-foreground">
                        {draftFilters.overUtilisedOnly
                          ? '≥ 100%'
                          : `${draftFilters.utilisationRange[0]}% – ${draftFilters.utilisationRange[1]}%`}
                      </span>
                    </div>
                    <Slider
                      min={0}
                      max={100}
                      step={5}
                      value={draftFilters.utilisationRange as [number, number]}
                      onValueChange={(v) => updateDraftFilter('utilisationRange', [v[0], v[1]])}
                      disabled={draftFilters.overUtilisedOnly}
                      className="w-full"
                    />
                    <div className="flex justify-between text-[11px] text-muted-foreground/60">
                      <span>0%</span>
                      <span>100%</span>
                    </div>
                  </div>
                </div>

                {/* Divider before Advanced */}
                <Separator />

                {/* ── Advanced ── */}
                <div className="pt-1 space-y-2">
                  <SectionHeading label="Advanced" />

                  <Button
                    variant={draftHasAdvancedFilters ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setAdvancedDialogOpen(true)}
                    className="w-full justify-between h-8 text-xs"
                  >
                    <span className="flex items-center gap-1.5">
                      <SlidersHorizontal className="h-3.5 w-3.5" />
                      Advanced Filters
                    </span>
                    {draftHasAdvancedFilters && (
                      <Badge
                        variant="secondary"
                        className="h-4 min-w-4 px-1 rounded-full text-[10px]"
                      >
                        {draftAdvancedFilters.conditions.length}
                      </Badge>
                    )}
                  </Button>

                  {hasHiddenBookings && (
                    <div className="flex items-center justify-between py-1">
                      <span className="text-sm text-foreground">Show filtered bookings</span>
                      <Switch
                        checked={showFilteredBookings}
                        onCheckedChange={setShowFilteredBookings}
                      />
                    </div>
                  )}
                </div>

              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>

          {/* Footer */}
          <SheetFooter className="px-4 py-3 border-t shrink-0 flex-row items-center justify-between gap-2">
            <Button
              variant="ghost"
              className="h-9 gap-1.5 text-muted-foreground hover:text-foreground px-2"
              onClick={handleClearAll}
              disabled={!canClearAll}
            >
              <X className="h-3.5 w-3.5" />
              Clear All
            </Button>
            <Button className="h-9 gap-1.5 px-5" onClick={handleApplyFilters} disabled={!canApply}>
              <Check className="h-3.5 w-3.5" />
              Apply Filters
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AdvancedFilterDialog
        open={advancedDialogOpen}
        onOpenChange={setAdvancedDialogOpen}
        draftAdvancedFilters={draftAdvancedFilters}
        onDraftApply={setDraftAdvancedFilters}
      />
    </>
  );
}
